import React, { useContext, useRef } from 'react';
import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { FileWithPath } from 'react-dropzone';
import { borderRadiusValues } from 'shared/lib/utils/uiConstants';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import each from 'lodash-es/each';
import { map } from 'lodash-es';

async function traverseFileTree(item: FileSystemEntry, path?: string) {
  let files: File[] = [];
  let p: Promise<void> | null = null;

  path = path || '';
  if (item.isFile) {
    const fileItem = item as FileSystemFileEntry;
    p = new Promise<void>((resolve) => {
      fileItem.file(function (file) {
        Object.defineProperty(file, 'webkitRelativePath', {
          get: function () {
            return path + file.name;
          },
        });
        files.push(file);
        resolve();
      });
    });
  } else if (item.isDirectory) {
    const dirItem = item as FileSystemDirectoryEntry;
    const dirReader = dirItem.createReader();

    p = new Promise<void>((resolve) => {
      dirReader.readEntries(async function (entries: any) {
        for (var i = 0; i < entries.length; i++) {
          const otherFiles = await traverseFileTree(
            entries[i],
            path + item.name + '/'
          );
          files = [...files, ...otherFiles];
        }
        resolve();
      });
    });
  }

  if (p) {
    await p;
  }

  return files;
}

export default function FolderSupportDropboxInner({
  onDrop,
  sx,
  text = '',
  buttonText = 'Загрузить',
  icon,
  triggerMode,
}: {
  onDrop: (acceptedFiles: FileWithPath[]) => void;
  sx?: SxProps;
  text?: string;
  buttonText?: string;
  icon?: React.ReactNode;
  triggerMode?: boolean;
}) {
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const ref = useRef<null | HTMLInputElement>(null);

  return (
    <Box
      sx={{
        display: triggerMode ? 'none' : 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        paddingY: 5,
        px: 10,
        width: 1,
        // border: `2px dashed ${dashColor}`,
        border: `2px dashed gray`,
        borderRadius: borderRadiusValues.medium,
        ...sx,
      }}
      onDrop={(e) => {
        if (e.dataTransfer.items) {
          each(e.dataTransfer.items, async (item) => {
            const folder = item.webkitGetAsEntry();

            if (folder && folder.isFile) {
              return;
            }

            if (folder) {
              const files = await traverseFileTree(folder);
              onDrop(files);
            }
          });
        }
      }}
    >
      <input
        style={{ display: 'none' }}
        type="file"
        onChange={(e) => {
          if (e.target.files) {
            onDrop(map(e.target.files, (file) => file));
          }
        }}
        ref={(node) => {
          if (node) {
            [
              'webkitdirectory',
              'directory',
              'mozdirectory',
              'odirectory',
            ].forEach((attr) => {
              node.setAttribute(attr, '');
            });
            ref.current = node;
          }
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: sp.s40,
          maxWidth: 470,
        }}
      >
        {icon}
        <Typography variant="f24" color={theme.palette.text.secondary}>
          {text}
        </Typography>
        <PrimaryButton
          variant="contained"
          type="button"
          onClick={() => {
            ref.current?.click();
          }}
          sx={{
            minWidth: 275,
          }}
        >
          {buttonText}
        </PrimaryButton>
      </Box>
    </Box>
  );
}
